/* eslint-disable global-require */
import axios from "axios";
import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Col,
  Row,
  Image,
  Form,
  Container,
  Button,
  Modal,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faArrowLeft } from "@fortawesome/fontawesome-free-solid";
import { getCookies } from "../../hooks/useCookies";
import { TableContext } from "../../contexts/tableContext";
import { confirmAlert } from "react-confirm-alert";
import { toast } from "react-toastify";
import styles from "./viewPopup.module.scss";
import { DateFormatter } from "../../utilityFunctions/utilsFunctions";
import envValues from "../../enviornment";
import { Entry } from "../../redux/entrySlice";
import { useDispatch } from "react-redux";
import Loader from "../../components/Loader/Loader";

const ViewTicketPopup = React.memo(({ ticketId, handleClose }) => {
  const { dashboardStyle } = useContext(TableContext);
  const { formthemeStyle, bodyheader, viewformStyle } =
    useContext(TableContext);
  const { setDashboardHeader } = useContext(TableContext);
  const { bodyStyle } = useContext(TableContext);
  const dispatch = useDispatch();
  const params = useParams();
  const token = `Bearer ${getCookies("Token")}`;
  const [loading, setLoading] = useState(false);
  const [details, setDetails] = useState([]);
  const dateFormat = getCookies("dateFormat");
  const [canDelete, setCanDelete] = useState(true);
  const [acceptSubmitting, setAcceptSubmitting] = useState(false);
  const [rejectSubmitting, setRejectSubmitting] = useState(false);
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [courtesyCreditSubmitting, setCourtesyCreditSubmitting] =
    useState(false);

  const [commentText, setCommentText] = useState("");

  // New state for courtesy credit modal
  const [showCourtesyCreditModal, setShowCourtesyCreditModal] = useState(false);
  const [courtesyCreditData, setCourtesyCreditData] = useState({
    amount: "",
    costAssignedTo: "baristi", // Default value
    note: "",
  });

  let statusCode;
  const getUserDetails = async () => {
    setLoading(true);
    axios
      .get(`${envValues.REACT_APP_API_ENDPOINT}/tickets/${ticketId}`, {
        method: "GET",
        headers: { Authorization: token },
      })
      .then((resp) => {
        statusCode = resp.status;
        return resp.data;
      })
      .then((res) => {
        if (statusCode === 200) {
          console.log("res.data.user", res.data.ticket);
          setDetails(res.data.ticket);
          setLoading(false);
        } else {
          setLoading(false);
        }
      })
      .catch((err) => {
        setLoading(false);
        return err;
      });
  };

  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    getUserDetails();
  }, []);

  const getMaxCreditAmount = () => {
    if (details?.orderId?.totalAmount) {
      return parseFloat(details?.orderId?.totalAmount);
    }
    if (details?.refundAmount) {
      return parseFloat(details.refundAmount);
    }
    return 0;
  };

  const validateCreditAmount = (amount) => {
    const maxAmount = getMaxCreditAmount();
    const inputAmount = parseFloat(amount);

    if (isNaN(inputAmount) || inputAmount <= 0) {
      return "Please enter a valid amount greater than 0";
    }

    if (inputAmount > maxAmount) {
      return `Amount cannot exceed order total of $${maxAmount.toFixed(2)}`;
    }

    return null;
  };

  const onReplyComment = async () => {
    if (!commentText.trim()) return;

    const apiData = {
      comment: commentText,
    };

    const data = {
      actionUrl: `tickets/add-comment/${ticketId}`,
      actionMethod: "patch",
      apiData,
    };

    try {
      const resp = await dispatch(Entry(data));

      if (resp?.payload?.code === 200) {
        toast.success("Comment added successfully");
        getUserDetails();
      } else {
        toast.error("Error adding comment");
      }
    } catch (error) {
      console.error("Error in submitting comment:", error);
      toast.error("Something went wrong");
    } finally {
      setCommentText("");
    }
  };

  const onInitiateRefund = async () => {
    setAcceptSubmitting(true);
    const data = {
      actionUrl: `tickets/initiate-refund/${ticketId}`,
      actionMethod: "patch",
    };

    try {
      const resp = await dispatch(Entry(data));

      if (resp?.payload?.code === 200) {
        toast.success("Refund completed successfully");
        getUserDetails();
      } else {
        toast.error("Error initiating refund");
      }
    } catch (error) {
      console.error("Error in submitting comment:", error);
      toast.error("Something went wrong");
    } finally {
      setAcceptSubmitting(false);
    }
  };

  const handleAcceptRefund = () => {
    confirmAlert({
      title: "Confirm Approval",
      message: `Are you sure you want to approve this refund? The refund amount of $${
        details?.refundAmount ?? 0
      } will be processed and the cost will be assigned to Baristi.`,
      buttons: [
        {
          label: "Yes, Approve",
          onClick: () => onInitiateRefund(),
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const onInitiateRefundCafe = async () => {
    setRejectSubmitting(true);
    const data = {
      actionUrl: `tickets/initiate-refund-cafe/${ticketId}`,
      actionMethod: "patch",
    };

    try {
      const resp = await dispatch(Entry(data));

      if (resp?.payload?.code === 200) {
        toast.success("Refund completed successfully");
        getUserDetails();
      } else {
        toast.error("Error initiating refund");
      }
    } catch (error) {
      console.error("Error in submitting comment:", error);
      toast.error("Something went wrong");
    } finally {
      setRejectSubmitting(false);
    }
  };

  const handleRejectRefund = () => {
    confirmAlert({
      title: "Confirm Rejection",
      message: `Are you sure you want to reject this refund? The refund amount of $${
        details?.refundAmount ?? 0
      } will be processed and the cost will be assigned to Café.`,
      buttons: [
        {
          label: "Yes, Reject",
          onClick: () => onInitiateRefundCafe(),
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  const onCancelRefund = async () => {
    setCancelSubmitting(true);
    const data = {
      actionUrl: `tickets/cancel-refund/${ticketId}`,
      actionMethod: "patch",
    };

    try {
      const resp = await dispatch(Entry(data));

      if (resp?.payload?.code === 200) {
        toast.success("Ticket closed successfully");
        getUserDetails();
      } else {
        toast.error("Error closing ticket");
      }
    } catch (error) {
      toast.error("Something went wrong");
    } finally {
      setCancelSubmitting(false);
    }
  };

  const handleCloseTicket = () => {
    confirmAlert({
      title: "Confirm Close Ticket",
      message:
        "Are you sure you want to close this ticket without processing a refund? This action cannot be undone.",
      buttons: [
        {
          label: "Yes, Close Ticket",
          onClick: () => onCancelRefund(),
        },
        {
          label: "Cancel",
          onClick: () => {},
        },
      ],
    });
  };

  // New function for issuing courtesy credit
  const onIssueCourtesyCredit = async () => {
    const validationError = validateCreditAmount(courtesyCreditData.amount);
    if (validationError) {
      toast.error(validationError);
      return;
    }

    setCourtesyCreditSubmitting(true);

    const apiData = {
      amount: parseFloat(courtesyCreditData.amount),
      costAssignedTo: courtesyCreditData.costAssignedTo,
    };

    const data = {
      actionUrl: `tickets/issue-courtesy-credit/${ticketId}`,
      actionMethod: "patch",
      apiData,
    };

    try {
      const resp = await dispatch(Entry(data));

      if (resp?.payload?.code === 200) {
        toast.success("Courtesy credit issued successfully");
        setShowCourtesyCreditModal(false);
        setCourtesyCreditData({
          amount: "",
          costAssignedTo: "Baristi",
        });
        getUserDetails();
      } else {
        toast.error("Error issuing courtesy credit");
      }
    } catch (error) {
      console.error("Error in issuing courtesy credit:", error);
      toast.error("Something went wrong");
    } finally {
      setCourtesyCreditSubmitting(false);
    }
  };

  // Open courtesy credit modal with pre-filled amount
  const handleOpenCourtesyCreditModal = () => {
    const maxAmount = getMaxCreditAmount();
    setCourtesyCreditData({
      amount: maxAmount > 0 ? maxAmount.toString() : "",
      costAssignedTo: "Baristi",
    });
    setShowCourtesyCreditModal(true);
  };

  const handleCloseCourtesyCreditModal = () => {
    setShowCourtesyCreditModal(false);
    setCourtesyCreditData({
      amount: "",
      costAssignedTo: "Baristi",
    });
  };

  // Handle amount change with validation
  const handleAmountChange = (value) => {
    setCourtesyCreditData({
      ...courtesyCreditData,
      amount: value,
    });
  };

  const maxCreditAmount = getMaxCreditAmount();
  const validationError = courtesyCreditData.amount
    ? validateCreditAmount(courtesyCreditData.amount)
    : null;

  return (
    <div className={styles.popupbox}>
      <div className={styles[formthemeStyle]} id={styles.box}>
        <span
          className={styles.closeicon}
          role="button"
          tabIndex={0}
          onClick={handleClose}
          onKeyDown={handleClose}
        >
          <FontAwesomeIcon icon={faTimes} />
        </span>

        {loading ? (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "200px" }}
          >
            <Loader />
          </div>
        ) : (
          <Container fluid>
            <Row>
              <h5 className={styles[bodyheader]} id={styles.addheading}>
                Ticket Details
              </h5>
            </Row>

            <Row className="mt-3">
              <Col lg={12}>
                <div className={styles.detaildiv}>
                  <Row>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Name</span>
                        <p className={styles.empname}>{details?.user?.name}</p>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Email</span>
                        <p className={styles.empname}>
                          {details?.user?.email ?? "NA"}
                        </p>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Order Id</span>
                        <p className={styles.empname}>
                          {details?.order_id ?? "NA"}
                        </p>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Description</span>
                        <p className={styles.empname}>
                          {details?.description ?? "NA"}
                        </p>
                      </div>
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Posted On</span>
                        <p className={styles.empname}>
                          {details?.createdAt
                            ? DateFormatter(
                                details?.createdAt,
                                dateFormat || "MonthDayYear"
                              )
                            : "NA"}
                        </p>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Refund Amount</span>
                        <p className={styles.empname}>
                          ${details?.refundAmount ?? 0}
                        </p>
                      </div>
                    </Col>
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Status</span>
                        <p className={styles.empname}>{details?.status}</p>
                      </div>
                    </Col>
                    {details?.courtesyCreditAmount && (
                      <Col lg={3} md={6} sm={12}>
                        <div
                          className={styles[viewformStyle]}
                          id={styles.empdiv}
                        >
                          <span className={styles.title}>
                            Courtesy Credit Amount
                          </span>
                          <p className={styles.empname}>
                            ${details?.courtesyCreditAmount}
                          </p>
                        </div>
                      </Col>
                    )}
                    <Col lg={3} md={6} sm={12}>
                      <div className={styles[viewformStyle]} id={styles.empdiv}>
                        <span className={styles.title}>Total Amount</span>
                        <p className={styles.empname}>${details?.orderId?.totalAmount}</p>
                      </div>
                    </Col>
                  </Row>
                </div>
              </Col>
            </Row>

            {/* Comments Section */}
            <Row className="mt-4">
              <Col lg={12}>
                <h6 className={styles.commentTitle}>Comments</h6>
                <div className={styles.commentsBox}>
                  {details?.comments?.length > 0 ? (
                    details.comments.map((comment, index) => (
                      <div key={index} className={styles.commentItem}>
                        <div className={styles.commentHeader}>
                          <strong className={styles.commentAuthor}>
                            {comment?.admin?.name ||
                              comment?.user?.name ||
                              "Unknown"}
                            :
                          </strong>
                          <span className={styles.commentMessage}>
                            {comment.message}
                          </span>
                        </div>
                        <div className={styles.commentedTime}>
                          {comment?.commentedAt
                            ? DateFormatter(
                                comment?.commentedAt,
                                dateFormat || "MonthDayYear"
                              )
                            : "NA"}
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No comments yet.</p>
                  )}
                </div>

                {/* Reply Form */}
                <Form className="mt-3">
                  <Form.Group controlId="comment">
                    <Form.Label>Reply as Admin</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      placeholder="Type your message here..."
                      value={commentText}
                      onChange={(e) => setCommentText(e.target.value)}
                    />
                  </Form.Group>
                  <Button
                    variant="primary"
                    className="mt-2"
                    disabled={!commentText.trim()}
                    onClick={() => {
                      onReplyComment(commentText);
                      setCommentText("");
                    }}
                  >
                    Submit Comment
                  </Button>
                </Form>
              </Col>
            </Row>

            {/* Action Buttons */}
            {details?.status === "Open" && (
              <Row className="mt-4">
                <Col className="d-flex justify-content-end gap-2">
                  <Button
                    variant="success"
                    onClick={handleAcceptRefund}
                    disabled={acceptSubmitting}
                  >
                    {acceptSubmitting ? "Processing..." : "Approve"}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleRejectRefund}
                    disabled={rejectSubmitting}
                  >
                    {rejectSubmitting ? "Processing..." : "Reject"}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCloseTicket}
                    disabled={cancelSubmitting}
                  >
                    {cancelSubmitting ? "Processing..." : "Close Ticket"}
                  </Button>
                  {/* New Courtesy Credit Button */}
                  <Button
                    variant="warning"
                    onClick={handleOpenCourtesyCreditModal}
                    disabled={courtesyCreditSubmitting}
                  >
                    Issue Courtesy Credit
                  </Button>
                </Col>
              </Row>
            )}
          </Container>
        )}
      </div>

      {/* Courtesy Credit Modal */}
      <Modal
        show={showCourtesyCreditModal}
        onHide={handleCloseCourtesyCreditModal}
      >
        <Modal.Header closeButton>
          <Modal.Title>Issue Courtesy Credit</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* <p className="text-muted mb-3">
            Manually entered credit up to full value of transaction added to
            customer's BarCard. Cost assigned either to Baristi or Café.
          </p> */}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Credit Amount *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max={maxCreditAmount}
                placeholder={`Enter credit amount (max: $${maxCreditAmount.toFixed(
                  2
                )})`}
                value={courtesyCreditData.amount}
                onChange={(e) => handleAmountChange(e.target.value)}
                isInvalid={!!validationError}
              />
              <Form.Text className="text-muted">
                Maximum allowed: ${maxCreditAmount.toFixed(2)}
              </Form.Text>
              {validationError && (
                <Form.Control.Feedback type="invalid">
                  {validationError}
                </Form.Control.Feedback>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cost Assigned To *</Form.Label>
              <Form.Select
                value={courtesyCreditData.costAssignedTo}
                onChange={(e) =>
                  setCourtesyCreditData({
                    ...courtesyCreditData,
                    costAssignedTo: e.target.value,
                  })
                }
              >
                <option value="Baristi">Baristi</option>
                <option value="Cafe">Café</option>
              </Form.Select>
            </Form.Group>

            {/* <Form.Group className="mb-3">
              <Form.Label>Note (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Add any additional notes..."
                value={courtesyCreditData.note}
                onChange={(e) =>
                  setCourtesyCreditData({
                    ...courtesyCreditData,
                    note: e.target.value,
                  })
                }
              />
            </Form.Group> */}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseCourtesyCreditModal}>
            Cancel
          </Button>
          <Button
            variant="warning"
            onClick={onIssueCourtesyCredit}
            disabled={
              courtesyCreditSubmitting ||
              !!validationError ||
              !courtesyCreditData.amount
            }
          >
            {courtesyCreditSubmitting ? "Processing..." : "Issue Credit"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
});

export default ViewTicketPopup;
